import { sortBy } from 'lodash';

import { api, isTruthy } from 'common';

import { EventAction } from '../../../utils/events/index';
import { FeatureFlagKey, type LaunchDarklyFlags } from '../../../utils/flags';
import { type Route, type RouteLink, type RouteSection } from '../common';

const { Permission } = api.Group;

export interface GetRoutesParams {
  user: api.User.UserWithOrganizations;
  permissions: api.Group.Permission[];
  launchDarklyFlags: LaunchDarklyFlags;
  hasAnyAgentAccess: boolean;
  hasAnyAgentGrantPermission: boolean;
}

export function getRoutes(params: GetRoutesParams): Route[] {
  const { user, permissions, launchDarklyFlags, hasAnyAgentAccess, hasAnyAgentGrantPermission } =
    params;
  const isAdmin = user.role === api.User.Role.Admin;
  const isAnalyst = user.role === api.User.Role.Analyst;
  const isStaff = user.role === api.User.Role.Staff;

  const { flowsPageMode } = launchDarklyFlags[FeatureFlagKey.deprecateAgentPages];
  const connectorsOptions = launchDarklyFlags[FeatureFlagKey.connectorsOptions];
  const triggerOptions = launchDarklyFlags[FeatureFlagKey.triggerOptions];

  const some = (...requiredPermissions: api.Group.Permission[]): boolean => {
    return requiredPermissions.some((p) => permissions.includes(p));
  };

  const every = (...requiredPermissions: api.Group.Permission[]): boolean => {
    return requiredPermissions.every((p) => permissions.includes(p));
  };

  const routes: Route[] = [];

  const analyzeItems: RouteLink[] = [];

  if (some(Permission.ViewDashboardEmbedsIncludingPhi, Permission.ViewDashboardEmbedsNoPHI)) {
    analyzeItems.push({
      label: 'Reports',
      to: '/reports/dashboards',
      keywords: ['analyze', 'analytics', 'dashboards'],
    });
    if (isTruthy(launchDarklyFlags[FeatureFlagKey.omniContentEmbedMode].isEnabled)) {
      analyzeItems.push({
        label: 'Create',
        to: '/reports/create',
      });
    }
  }

  if (analyzeItems.length > 0) {
    routes.push({
      label: 'Analyze',
      sections: [{ label: '', links: analyzeItems, priorityOrder: 2 }],
    });
  }

  const buildItems: RouteLink[] = [];
  if (hasAnyAgentAccess) {
    if (flowsPageMode === 'LegacyAgentsPage') {
      buildItems.push({
        label: 'Agents',
        to: '/agents/agents',
        keywords: ['agents', 'flows', 'agent', 'flow', 'builder', 'build', 'edit agent'],
        eventAction: EventAction.AgentsLinkClick,
      });
    }
    buildItems.push({
      label: 'Flows',
      to: '/agents/flows',
      keywords: [
        'flows',
        'flow',
        'agent',
        'flow definitions',
        'builder',
        'edit flows',
        'flow insights',
      ],
      eventAction: EventAction.FlowsLinkClick,
    });
    if (some(Permission.ModifyFlowBuilderSkills, Permission.PublishFlowBuilderSkills)) {
      buildItems.push({
        label: 'Skills',
        to: '/agents/skills',
        keywords: ['skills'],
        eventAction: EventAction.FlowsLinkClick,
      });
    }
  }

  if (some(Permission.ViewKnowledgeBaseFiles)) {
    buildItems.push({
      label: 'Knowledge Bases',
      to: '/knowledge-base',
      keywords: ['knowledge', 'documents', 'docs', 'content', 'kb', 'files', 'assets', 'sources'],
      eventAction: EventAction.KnowledgeBasesLinkClick,
    });
  }

  if (some(Permission.ViewAgents, Permission.ViewFlowBuilderAncillary)) {
    buildItems.push({
      label: 'Reference Tables',
      to: '/agents/flows/referenceTables',
      keywords: ['reference', 'lookup', 'tables', 'mappings', 'flow references', 'data'],
      eventAction: EventAction.ReferenceTablesLinkClick,
    });
  }

  if (some(Permission.ViewVoiceAssistant)) {
    buildItems.push({
      label: 'Assistants',
      to: '/assistants',
      keywords: ['voice', 'assistants', 'AI', 'calls', 'phone', 'conversations'],
      eventAction: EventAction.VoiceAssistantsLinkClick,
    });
  }

  if (some(Permission.ViewAgents, Permission.ViewFlowBuilderAncillary)) {
    buildItems.push({
      label: 'Runs',
      to: '/agents/flows/runs',
      eventAction: EventAction.RunsLinkClick,
    });
  }

  if (some(Permission.ViewScenarios)) {
    buildItems.push({
      label: 'Scenarios',
      to: '/fingerprint/scenarios',
      keywords: ['automations', 'scheduler', 'triggers', 'rules'],
      eventAction: EventAction.ScenariosLinkClick,
    });
  }

  if (some(Permission.ModifyFlowStandardEvents)) {
    buildItems.push({
      label: 'Standard Events',
      to: '/agents/flows/standardAnalyticEvents',
    });
    buildItems.push({
      label: 'Standard Categories',
      to: '/agents/flows/standardAnalyticEvents/categories',
    });
  }

  if (triggerOptions.isEnabled) {
    buildItems.push({
      label: 'Triggers',
      to: '/triggers',
    });
  }

  if (buildItems.length > 0) {
    routes.push({
      label: 'Build',
      sections: [{ label: '', links: buildItems, priorityOrder: 1 }],
    });
  }

  const templatesItems: RouteLink[] = [];
  if ((isAdmin || isStaff) && some(Permission.Broadcast)) {
    templatesItems.push({
      label: 'Broadcast Messages',
      to: '/messaging/templates',
      keywords: ['templates', 'text', 'sms'],
    });
  }
  if ((isAdmin || isStaff) && some(Permission.PreviewDocumentTemplate)) {
    templatesItems.push({
      label: 'Documents',
      to: '/document-templates',
      keywords: ['document templates', 'notes', 'docs', 'signer'],
    });
  }
  if ((isAdmin || isStaff) && some(Permission.ViewPrevisitLinks)) {
    templatesItems.push({
      label: 'Pre-visit Links',
      to: '/previsit-links',
      keywords: ['previsit links', 'scheduling links', 'templates'],
    });
  }
  if (some(Permission.ViewPrevisitFormTemplates)) {
    templatesItems.push({
      label: 'Forms',
      to: '/previsit-form-templates',
      keywords: ['templates', 'form builder', 'primitives', 'patient', 'registration'],
    });
  }
  if (
    some(
      Permission.ViewNotificationContents,
      Permission.ViewNotificationSchemes,
      Permission.ViewNotificationSchedules
    )
  ) {
    templatesItems.push({
      label: 'Notifications',
      to: '/notifications',
      keywords: [
        'templates',
        'messaging',
        'outreach',
        'schemes',
        'schedules',
        'email',
        'sms',
        'text',
        'messages',
        'voice',
        'call',
        'phone',
      ],
    });
  }
  if (some(Permission.ViewNoteTemplates)) {
    templatesItems.push({
      label: 'Notes',
      to: '/note-templates',
      keywords: ['Templates'],
    });
  }
  if (some(Permission.ViewAgentConversation, Permission.ModifyAgentConversationResponseTemplate)) {
    templatesItems.push({
      label: 'SMS Responses',
      to: '/tasks/response-templates',
      keywords: ['text', 'ai', 'templates', 'messages'],
    });
  }
  if (some(Permission.ViewRecommendationTemplates)) {
    templatesItems.push({
      label: 'Recommendations',
      to: '/recommendation-templates',
      keywords: ['care', 'recs', 'registration', 'patients'],
    });
  }
  if ((isAdmin || isStaff) && some(Permission.ViewDecisionTrees, Permission.ModifyDecisionTrees)) {
    templatesItems.push({
      label: 'Decision Trees',
      to: '/decision-trees',
      keywords: ['logic', 'scheduling', 'clinical questions'],
    });
  }

  routes.push({
    label: 'Templates',
    sections: [
      {
        label: 'Templates',
        links: sortBy(templatesItems, 'label'),
        priorityOrder: 6,
      },
    ],
  });

  const operateItems: RouteLink[] = [];
  if ((isAdmin || isStaff) && some(Permission.ViewEncounters)) {
    operateItems.push({
      label: 'Appointments',
      to: '/encounters',
      keywords: [
        'appt',
        'patients',
        'check-in',
        'checkin',
        'scheduling',
        'reschedule',
        'patient timeline',
      ],
    });
  }
  if ((isAdmin || isStaff) && some(Permission.Broadcast)) {
    operateItems.push({
      label: 'Messages',
      to: '/messaging/messages',
      keywords: ['broadcast', 'sms', 'outreach', 'campaign', 'texts', 'alerts', 'communication'],
    });
  }
  if (permissions.includes(Permission.ViewPayments)) {
    operateItems.push({
      label: 'Payments',
      to: '/payments/report',
      keywords: [
        'payment dashboard',
        'refunds',
        'cancel',
        'finance',
        'dispute',
        'operate',
        'billing',
      ],
    });
  }
  if (some(Permission.ViewReconciliationFeesReport)) {
    operateItems.push({
      label: 'Payout Adjustments',
      to: '/payouts/adjustments',
      keywords: ['payouts', 'reconciliation', 'report', 'billing', 'disputes'],
    });
    operateItems.push({
      label: 'Payout Reports',
      to: '/payouts/report',
      keywords: ['payouts', 'deposits', 'finance', 'reconciliation', 'billing'],
    });
  }
  if (some(Permission.ViewAgentConversation)) {
    operateItems.push({
      label: 'Conversation Tasks',
      to: '/tasks/agent-conversations',
      keywords: ['text', 'conversations', 'conversational AI', 'workqueue', 'queue'],
    });
  }
  if (some(Permission.ClaimHumanTasks, Permission.ClaimHumanTasksInCategories)) {
    operateItems.push({
      label: 'Tasks',
      to: '/human-tasks',
      keywords: ['human tasks', 'review tasks', 'queue', 'inbox', 'workqueue', 'sidekick'],
    });
  }
  if (some(Permission.ViewVoiceConversations)) {
    operateItems.push({
      label: 'Voice Logs',
      to: '/messaging/voice-calls',
      keywords: [
        'calls',
        'conversations',
        'transcripts',
        'call logs',
        'communication',
        'debug',
        'assistants',
        'phone call',
        'voice AI',
      ],
    });
  }
  if (some(Permission.ViewAdminDashboard)) {
    operateItems.push({
      label: 'Flow Activity',
      to: '/flow-activity',
      keywords: ['admin', 'monitoring', 'workers', 'temporal', 'activity', 'blocks', 'running'],
    });
  }

  routes.push({
    label: 'Operate',
    sections: [
      {
        label: '',
        links: operateItems,
        priorityOrder: 3,
      },
    ],
  });

  const integrationsItems: RouteLink[] = [];

  if (
    connectorsOptions.isListScreenEnabled &&
    every(Permission.ViewFeeds, Permission.ViewApiInstances)
  ) {
    integrationsItems.push({
      label: 'Connectors',
      to: '/connectors',
      keywords: ['connectors'],
    });
  }

  if (some(Permission.ViewApiInstances)) {
    integrationsItems.push({
      label: 'API Instances',
      to: '/api-instances',
      keywords: [
        'api',
        'integrations',
        'fhir',
        'connection',
        'connector',
        'connect',
        'data',
        'instances',
      ],
    });
  }
  if (some(Permission.ViewEhrInstances)) {
    integrationsItems.push({
      label: 'EHR Instances',
      to: '/ehr-instances',
      keywords: ['environments', 'sandboxes', 'instances', 'EHR', 'EMR'],
    });
  }
  if ((isAdmin || isStaff) && some(Permission.ViewEhrSystems)) {
    integrationsItems.push({
      label: 'EHR Systems',
      to: '/ehr-systems',
      keywords: ['vendor', 'epic', 'cerner', 'onboarding', 'organization', 'athena', 'EHR', 'EMR'],
    });
  }
  if (some(Permission.ViewFeeds)) {
    integrationsItems.push({
      label: 'Feeds',
      to: '/feeds',
      keywords: ['data sync', 'integrations', 'feed', 'fingerprint'],
    });
  }
  if (some(Permission.ViewOrchestratorConfigs)) {
    integrationsItems.push({
      label: 'Job Configs',
      to: '/job-configs',
      keywords: ['configs', 'jobs', 'connector', 'connect', 'data'],
    });
  }
  if (some(Permission.ViewPracticeConfigs)) {
    integrationsItems.push({
      label: 'Practice Configs',
      to: '/practice-configs',
      keywords: ['mappings', 'settings', 'mapping', 'pcei', 'pcai'],
    });
  }
  if (some(Permission.ViewPracticeConfigApiInstances)) {
    integrationsItems.push({
      label: 'Practice Configs (API)',
      to: '/practice-config-api-instances',
      keywords: ['mappings', 'settings', 'pcei', 'pcai'],
    });
  }
  if (some(Permission.ViewPracticeConfigEhrInstances)) {
    integrationsItems.push({
      label: 'Practice Configs (EHR)',
      to: '/practice-config-ehr-instances',
      keywords: ['EHR', 'mappings', 'settings', 'pcei', 'pcai'],
    });
  }
  if (some(Permission.ViewServiceAccount) && isAdmin) {
    integrationsItems.push({
      label: 'Service Accounts',
      to: '/service-accounts',
      keywords: ['credentials', 'automation', 'payments', 'billing'],
    });
  }
  if (isAdmin) {
    integrationsItems.push({
      label: 'Stripe Accounts',
      to: '/stripe-accounts',
      keywords: ['billing', 'payouts', 'payments', 'refunds'],
    });
  }

  if (integrationsItems.length > 0) {
    routes.push({
      label: 'Connect',
      sections: [
        {
          label: '',
          links: integrationsItems,
          priorityOrder: 4,
        },
      ],
    });
  }

  const mappingItems: RouteLink[] = [];

  if (
    some(Permission.ViewCanonicalFieldTypes) &&
    some(Permission.ViewCanonicalFields) &&
    some(Permission.ViewAppointmentKinds)
  ) {
    mappingItems.push({
      label: 'Appointment Kinds',
      to: '/appointment-kinds',
      keywords: ['mappings', 'appt'],
    });
  }
  if (isAdmin) {
    mappingItems.push({
      label: 'Note Sections',
      to: '/notes/standard-sections',
      keywords: ['mappings', 'notes', 'provider'],
    });
  }
  if (some(Permission.ViewPayors)) {
    mappingItems.push({
      label: 'Payors',
      to: '/payors',
      keywords: ['mappings', 'payers', 'insurance'],
    });
  }
  if ((isAdmin || isStaff) && some(Permission.ViewPopulations)) {
    mappingItems.push({
      label: 'Populations',
      to: '/fingerprint/populations',
      keywords: ['mappings', 'patients', 'community', 'communities'],
    });
  }

  if (isAdmin) {
    mappingItems.push({
      label: 'Procedures',
      to: '/fingerprint/procedures',
      keywords: ['mappings', 'ICD-10', 'CPT'],
    });
    if (some(Permission.ViewVisitReasons)) {
      mappingItems.push({
        label: 'Visit Reasons',
        to: '/fingerprint/visit-reasons',
        keywords: ['mappings', 'appointment'],
      });
    }
  }

  if (mappingItems.length > 0) {
    routes.push({
      label: 'Mappings',
      sections: [
        {
          label: '',
          links: mappingItems,
          priorityOrder: 7,
        },
      ],
    });
  }

  const manageItems: RouteLink[] = [];
  if ((isAdmin || isStaff) && some(Permission.ViewFrontDeskDevices)) {
    manageItems.push({
      label: 'Front Desk Devices',
      to: '/front-desk-devices',
      keywords: ['check-in tablets', 'kiosk', 'ipads', 'front-desk'],
    });
  }
  if (isAdmin || hasAnyAgentGrantPermission) {
    manageItems.push({
      label: 'Permissions',
      to: '/permission-groups',
      keywords: ['permissions', 'roles', 'access control', 'RBAC', 'users', 'access', 'perms'],
    });
  }
  if (some(Permission.ViewAssistantPhoneConfiguration)) {
    manageItems.push({
      label: 'Phone Numbers',
      to: '/phone-numbers',
      keywords: [
        'org',
        'organization',
        'manage',
        'assistant',
        'assistants',
        'voice',
        'AI',
        'inbound',
        'outbound',
      ],
    });
  }
  if ((isAdmin || isStaff) && some(Permission.ViewPractices)) {
    manageItems.push({
      label: 'Practices',
      to: '/practices',
      keywords: ['facilities', 'locations', 'offices', 'clinics', 'clinic, office', 'facility'],
    });
  }
  if (isAdmin || ((isAnalyst || isStaff) && some(Permission.ViewProvider))) {
    manageItems.push({
      label: 'Providers',
      to: '/providers',
      keywords: ['clinicians', 'doctors', 'activate'],
    });
  }
  if (some(Permission.ViewUsers) && isAdmin) {
    manageItems.push({
      label: 'Users',
      to: '/users',
      keywords: [
        'accounts',
        'staff',
        'internal',
        'external',
        'permissions',
        'manage',
        'access',
        'roles',
        'analyst',
      ],
    });
  }
  if (some(Permission.ViewAndModifyHumanTaskCategories)) {
    manageItems.push({
      label: 'Task Categories',
      to: '/human-task-categories',
      keywords: ['human tasks', 'group', 'category', 'groups'],
    });
  }

  if (manageItems.length > 0) {
    routes.push({
      label: 'Manage',
      sections: [
        {
          label: '',
          links: manageItems,
          priorityOrder: 5,
        },
      ],
    });
  }

  const toolsItems: RouteLink[] = [];
  if ((isAdmin || isStaff) && some(Permission.ViewOrchestratorConfigs)) {
    toolsItems.push({
      label: 'Retrieve Appointments',
      to: '/diagnostics/retrieve-appointments',
      keywords: ['appts', 'scheduling', 'slots'],
    });
  }
  if (some(Permission.ViewExternalDocumentPackets)) {
    toolsItems.push({
      label: 'Document Packets',
      to: '/external-doc-packets',
      keywords: ['doc', 'documents', 'referrals'],
    });
  }
  if (some(Permission.ViewAgents, Permission.ViewFlowBuilderAncillary)) {
    toolsItems.push({
      label: 'Document Upload',
      to: '/document-upload',
      keywords: ['doc', 'documents', 'uploader', 'hosting'],
    });
  }
  if (some(Permission.ViewEmailSubscription)) {
    toolsItems.push({
      label: 'Contact Status (Email)',
      to: '/contact-status/email',
      keywords: ['subscribe', 'unsubscribe'],
    });
  }
  if (isAdmin && some(Permission.UpdatePatientFindings)) {
    toolsItems.push({
      label: 'Findings',
      to: '/findings',
      keywords: ['patient', 'clinical', 'decisions', 'AI'],
    });
  }
  if (some(Permission.TestWorkflow)) {
    toolsItems.push({
      label: 'Flow Tester',
      to: '/flow-tester',
      keywords: ['testing', 'build', 'qa', 'patient', 'encounter', 'order', 'document packet'],
    });
  }
  if (isAdmin || user.isImpersonating) {
    toolsItems.push({
      label: 'Impersonation',
      to: '/impersonation',
      keywords: ['impersonate', 'testing', 'qa', 'user', 'mock', 'mirror'],
    });
  }
  if (some(Permission.ViewPhoneNumberStatus)) {
    toolsItems.push({
      label: 'Contact Status (Phone)',
      to: '/contact-status/phone',
      keywords: ['subscribe', 'unsubscribe'],
    });
  }
  if (some(Permission.ViewAgents, Permission.ViewFlowBuilderAncillary)) {
    toolsItems.push({ label: 'Signals', to: '/signals', keywords: ['work', 'flow signals'] });
  }

  if (some(Permission.ModifyAssistantConfiguration)) {
    toolsItems.push({
      label: 'Web Assistants',
      to: '/assistant',
      keywords: ['embed', 'conversation', 'AI'],
    });
  }

  const moreSections: RouteSection[] = [
    {
      label: 'Tools',
      links: sortBy(toolsItems, 'label'),
      priorityOrder: 8,
    },
  ];

  routes.push({
    label: 'Tools',
    sections: moreSections,
  });

  return routes.filter((navItem) => {
    if ('sections' in navItem) {
      // remove empty sections
      navItem.sections = navItem.sections.filter((section) => section.links.length > 0);

      // remove dropdowns with no sections
      if (navItem.sections.length === 0) {
        return false;
      }

      // don't show labels on only-child sections
      if (navItem.sections.length === 1) {
        navItem.sections[0].label = '';
      }
    }
    return true;
  });
}
